import React from 'react';
import { clsx } from 'clsx';
import { Calendar, Filter, X } from 'lucide-react';
import { DashboardFilter } from '../../types/dashboard';

interface FilterBarProps {
  filter: DashboardFilter;
  onChange: (filter: DashboardFilter) => void;
  showDepartment?: boolean;
  showStatus?: boolean;
  showPriority?: boolean;
  departments?: { id: string; name: string }[];
  className?: string;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filter,
  onChange,
  showDepartment = false,
  showStatus = false,
  showPriority = false,
  departments = [],
  className,
}) => {
  const hasActiveFilters = filter.date_from || filter.date_to || filter.department_id || filter.status || filter.priority;

  const clearFilters = () => {
    onChange({});
  };

  return (
    <div className={clsx('flex flex-wrap items-center gap-3', className)}>
      <div className="flex items-center gap-1.5 text-slate-400">
        <Filter className="w-4 h-4" />
        <span className="text-xs font-medium">Filters:</span>
      </div>

      <div className="relative">
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
        <input
          type="date"
          value={filter.date_from || ''}
          onChange={e => onChange({ ...filter, date_from: e.target.value || undefined })}
          className="pl-8 pr-3 py-1.5 bg-slate-900/60 border border-slate-700/60 rounded-lg text-xs text-slate-300 focus:outline-none focus:border-sky-500/50"
          placeholder="From"
        />
      </div>

      <div className="relative">
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
        <input
          type="date"
          value={filter.date_to || ''}
          onChange={e => onChange({ ...filter, date_to: e.target.value || undefined })}
          className="pl-8 pr-3 py-1.5 bg-slate-900/60 border border-slate-700/60 rounded-lg text-xs text-slate-300 focus:outline-none focus:border-sky-500/50"
          placeholder="To"
        />
      </div>

      {showDepartment && departments.length > 0 && (
        <select
          value={filter.department_id || ''}
          onChange={e => onChange({ ...filter, department_id: e.target.value || undefined })}
          className="px-3 py-1.5 bg-slate-900/60 border border-slate-700/60 rounded-lg text-xs text-slate-300 focus:outline-none focus:border-sky-500/50"
        >
          <option value="">All Departments</option>
          {departments.map(dept => (
            <option key={dept.id} value={dept.id}>{dept.name}</option>
          ))}
        </select>
      )}

      {showStatus && (
        <select
          value={filter.status || ''}
          onChange={e => onChange({ ...filter, status: e.target.value || undefined })}
          className="px-3 py-1.5 bg-slate-900/60 border border-slate-700/60 rounded-lg text-xs text-slate-300 focus:outline-none focus:border-sky-500/50"
        >
          <option value="">All Statuses</option>
          <option value="New">New</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="Closed">Closed</option>
        </select>
      )}

      {showPriority && (
        <select
          value={filter.priority || ''}
          onChange={e => onChange({ ...filter, priority: e.target.value || undefined })}
          className="px-3 py-1.5 bg-slate-900/60 border border-slate-700/60 rounded-lg text-xs text-slate-300 focus:outline-none focus:border-sky-500/50"
        >
          <option value="">All Priorities</option>
          <option value="Critical">Critical</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
      )}

      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-rose-400 hover:text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg transition-colors"
        >
          <X className="w-3 h-3" />
          Clear
        </button>
      )}
    </div>
  );
};
